import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LogoPaperCrane from '../Logo/LogoPaperCrane';
import LanguageToggle from './LanguageToggle';
import { useTranslation } from '../../hooks/useTranslation';
import styles from './Header.module.css';

const Header = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const [isAtTop, setIsAtTop] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const isHomePage = location.pathname === '/';

  // Track scroll position with hysteresis to prevent flickering
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      if (isAtTop && scrollY > 50) {
        setIsAtTop(false);
      } else if (!isAtTop && scrollY < 10) {
        setIsAtTop(true);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    setIsAtTop(window.scrollY < 50);

    return () => window.removeEventListener('scroll', handleScroll);
  }, [isAtTop]);

  // Reset on navigation
  useEffect(() => {
    setIsAtTop(window.scrollY < 50);
    setIsMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path) => {
    return location.pathname === path ? styles.active : '';
  };

  // Force solid header when mobile menu is open so the X button stays readable
  const isTransparent = isHomePage && isAtTop && !isMenuOpen;

  const headerClasses = [
    styles.header,
    isTransparent ? styles.transparent : styles.solid
  ].join(' ');

  const logoColor = isTransparent ? '#ffffff' : '#1a1a1a';
  const logoAccent = '#D2462F';

  const closeMenu = () => setIsMenuOpen(false);

  return (
    <header className={headerClasses}>
      <div className={styles.container}>
        <Link to="/" className={styles.logo}>
          <LogoPaperCrane
            size={50}
            color={logoColor}
            accentColor={logoAccent}
          />
          <h1>Antoine Pierson</h1>
        </Link>

        {/* Desktop nav — hidden on mobile */}
        <nav className={styles.nav}>
          <Link to="/portfolio" className={`${styles.navLink} ${isActive('/portfolio')}`}>
            {t('header.portfolio')}
          </Link>
          <Link to="/stories" className={`${styles.navLink} ${isActive('/stories')}`}>
            {t('header.stories')}
          </Link>
          <Link to="/about" className={`${styles.navLink} ${isActive('/about')}`}>
            {t('header.about')}
          </Link>
          <Link to="/contact" className={`${styles.navLink} ${isActive('/contact')}`}>
            {t('header.contact')}
          </Link>
          <LanguageToggle transparent={isTransparent} />
        </nav>

        {/* Mobile: language toggle + hamburger button */}
        <div className={styles.mobileControls}>
          <LanguageToggle transparent={isTransparent} />
          <button
            className={styles.menuButton}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
          >
            <span className={`${styles.bar} ${isMenuOpen ? styles.barTopOpen : ''}`} />
            <span className={`${styles.bar} ${isMenuOpen ? styles.barMidOpen : ''}`} />
            <span className={`${styles.bar} ${isMenuOpen ? styles.barBotOpen : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      {isMenuOpen && (
        <nav className={styles.mobileMenu}>
          <Link to="/portfolio" className={`${styles.mobileNavLink} ${isActive('/portfolio')}`} onClick={closeMenu}>
            {t('header.portfolio')}
          </Link>
          <Link to="/stories" className={`${styles.mobileNavLink} ${isActive('/stories')}`} onClick={closeMenu}>
            {t('header.stories')}
          </Link>
          <Link to="/about" className={`${styles.mobileNavLink} ${isActive('/about')}`} onClick={closeMenu}>
            {t('header.about')}
          </Link>
          <Link to="/contact" className={`${styles.mobileNavLink} ${isActive('/contact')}`} onClick={closeMenu}>
            {t('header.contact')}
          </Link>
        </nav>
      )}
    </header>
  );
};

export default Header;
